import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { normalizeGroupBy, stringifyGroupBy } from './groupBy';

const VIEWS = ['summary', 'table', 'cards', 'detail'];

const parsePositiveInt = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
};

const parseSortDir = (value, fallback = 'desc') => (value === 'asc' ? 'asc' : fallback);

const parseView = (value) => (VIEWS.includes(value) ? value : 'summary');

const fromParams = (params) => ({
  store_id: params.get('store_id') || '',
  date_from: params.get('date_from') || '',
  date_to: params.get('date_to') || '',
  status: params.get('status') || '',
  search: params.get('search') || '',
  group_fields: normalizeGroupBy(params.get('group_by') || 'client'),
  view: parseView(params.get('view') || 'summary'),
  sort_by: params.get('sort_by') || 'count',
  sort_dir: parseSortDir(params.get('sort_dir') || 'desc'),
  page: parsePositiveInt(params.get('page'), 1),
  page_size: parsePositiveInt(params.get('page_size'), 25),
  leaf: params.get('leaf') || '',
  item_search: params.get('item_search') || '',
  item_sort_by: params.get('item_sort_by') || 'quantity',
  item_sort_dir: parseSortDir(params.get('item_sort_dir') || 'desc'),
  item_page: parsePositiveInt(params.get('item_page'), 1),
  item_page_size: parsePositiveInt(params.get('item_page_size'), 25)
});

const toParams = (state) => {
  const params = new URLSearchParams();

  const entries = {
    store_id: state.store_id,
    date_from: state.date_from,
    date_to: state.date_to,
    status: state.status,
    search: state.search,
    group_by: stringifyGroupBy(state.group_fields),
    view: state.view,
    sort_by: state.sort_by,
    sort_dir: state.sort_dir,
    page: state.page,
    page_size: state.page_size,
    leaf: state.leaf,
    item_search: state.item_search,
    item_sort_by: state.item_sort_by,
    item_sort_dir: state.item_sort_dir,
    item_page: state.item_page,
    item_page_size: state.item_page_size
  };

  Object.entries(entries).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      params.set(key, String(value));
    }
  });

  return params;
};

export const useOrdersDashboardFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState(() => fromParams(searchParams));
  const [debouncedSearch, setDebouncedSearch] = useState(() => fromParams(searchParams).search || '');

  const searchParamsKey = searchParams.toString();

  useEffect(() => {
    const parsed = fromParams(searchParams);
    setState((prev) => {
      const prevJSON = JSON.stringify(prev);
      const nextJSON = JSON.stringify(parsed);
      return prevJSON === nextJSON ? prev : parsed;
    });
  }, [searchParamsKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(state.search || '');
    }, 400);
    return () => clearTimeout(timer);
  }, [state.search]);

  useEffect(() => {
    const next = toParams(state);
    const nextKey = next.toString();
    if (nextKey !== searchParamsKey) {
      setSearchParams(next, { replace: true });
    }
  }, [state, searchParamsKey, setSearchParams]);

  const apiFilters = useMemo(() => ({
    store_id: state.store_id,
    date_from: state.date_from,
    date_to: state.date_to,
    status: state.status,
    search: debouncedSearch,
    group_by: stringifyGroupBy(state.group_fields)
  }), [state, debouncedSearch]);

  const actions = {
    patch: (patch) => setState((prev) => ({ ...prev, ...patch })),
    setView: (view) => setState((prev) => ({ ...prev, view: parseView(view) })),
    setGroupFields: (group_fields) => setState((prev) => ({ ...prev, group_fields: normalizeGroupBy(group_fields), page: 1, leaf: '' })),
    setMainSorting: (sort_by, sort_dir) => setState((prev) => ({ ...prev, sort_by, sort_dir, page: 1 })),
    setPagination: (page, page_size) => setState((prev) => ({ ...prev, page, page_size })),
    setLeafSelection: (leaf) => setState((prev) => ({ ...prev, leaf, view: leaf ? 'detail' : prev.view, item_page: 1 })),
    setItemControls: (patch) => setState((prev) => ({ ...prev, ...patch })),
    clearFilters: () => setState((prev) => ({
      ...prev,
      date_from: '',
      date_to: '',
      status: '',
      search: '',
      page: 1,
      item_search: '',
      item_page: 1,
      leaf: ''
    }))
  };

  return {
    state,
    apiFilters,
    debouncedSearch,
    actions
  };
};
